'use client';

import { FC, useEffect, useMemo, useRef } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  ColumnDef,
  flexRender,
  ColumnSizingState,
  OnChangeFn,
  Header
} from '@tanstack/react-table'
import { AlertCircle } from 'lucide-react'
import { Button, ScrollArea, ScrollBar, Input } from '@/components/ui'
import { TableDataJSON, DataRowJSON, columnsToRows } from '../../../src/types/table-json'
import { createDataTableStore, ValidationError, ColumnMeta, DataTableState } from './data-table-input.store'
import React from 'react'
import { cn } from '../../../lib/utils'

/**
 * CSS for the column resize handles
 * - Default state: invisible (opacity: 0)
 * - Hover state: visible with semi-transparent background
 * - Active/Resizing state: darker background
 * - Transition: smooth fade in/out for better UX
 * - !important flags ensure styles aren't overridden by other classes
 */
const resizerStyles = `
  .resizer {
    opacity: 0;
    transition: all 0.15s ease;
    cursor: col-resize;
    width: 4px !important;
  }
  
  .resizer:hover {
    opacity: 1;
    background: rgba(0, 0, 0, 0.5) !important;
  }
  
  .resizer.isResizing {
    opacity: 1;
    background: rgba(0, 0, 0, 0.8) !important;
  }

  /* Add padding to make the hover area larger */
  .resizer::after {
    content: '';
    position: absolute;
    right: -6px;
    width: 16px;
    height: 100%;
    cursor: col-resize;
  }
`

/**
 * DataTableInput component for editable table data entry
 * 
 * Width and Scrolling Behavior:
 * 1. The component is designed to work within a container of fixed width (e.g., 300px panel)
 * 2. Horizontal scrolling is enabled when total column widths exceed the container width
 * 3. To implement horizontal scrolling:
 *    - Wrap table in a ScrollArea component
 *    - Set table container width to max of (total column width, maxWidth)
 *    - Set minWidth to maxWidth to prevent container from shrinking below maxWidth
 *    - Individual column widths are controlled via the columnSizing state
 * 
 * Example Usage with Fixed Width Container:
 * ```tsx
 * <div className="w-[300px]">
 *   <DataTableInput
 *     maxWidth={300}
 *     initialColumns={[
 *       { id: 'col1', name: 'Column 1', width: 150 },
 *       { id: 'col2', name: 'Column 2', width: 150 },
 *       { id: 'col3', name: 'Column 3', width: 150 }
 *     ]}
 *   />
 * </div>
 * ```
 */
interface DataTableInputProps {
  /**
   * Called when data changes
   */
  onChange?: (data: TableDataJSON) => void
  /**
   * Initial data in TableDataJSON format. Required.
   * This defines both the initial columns and data.
   */
  initialData: TableDataJSON;
  /**
   * Minimum width for each column
   * @default 60
   */
  minColumnWidth?: number;
  /**
   * Default width for columns without a specified width
   * @default 120
   */
  defaultColumnWidth?: number;
  maxColumnNameLength?: number;
  /**
   * Maximum width of the table container in pixels
   * If content exceeds this width, horizontal scrolling will be enabled
   * This should match the width of the container element
   */
  maxWidth?: number;
  /**
   * Maximum height of the table container in pixels
   * If content exceeds this height, vertical scrolling will be enabled
   * @default 400
   */
  maxHeight?: number;
  /**
   * Optional store reference to share state between multiple instances
   */
  storeRef?: React.MutableRefObject<ReturnType<typeof createDataTableStore> | null>;
}

const ColumnHeader = React.memo(({ column, onRename, onKeyDown, store }: { 
  column: { id: string; meta: { name: string; width: number } }, 
  onRename: (id: string, value: string) => void,
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>, columnId: string) => void,
  store: ReturnType<typeof createDataTableStore>
}) => {
  const [value, setValue] = React.useState(column.meta?.name || '')
  const validationState = store.useValidationState()

  React.useEffect(() => {
    setValue(column.meta?.name || '')
  }, [column.meta?.name])

  const validationError = validationState[column.id]

  return (
    <div className="relative">
      <Input
        type="text"
        value={value}
        className={cn(
          "h-8 px-2 bg-transparent font-medium border-0 rounded-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-blue-500",
          validationError && "pr-8 border-red-500"
        )}
        placeholder="Column name..."
        onChange={e => {
          setValue(e.target.value)
          onRename(column.id, e.target.value)
        }}
        onKeyDown={e => onKeyDown(e, column.id)}
        data-testid={`header-${column.id}`}
        data-header={column.id}
      />
      {validationError && (
        <div 
          className="absolute right-2 top-1/2 -translate-y-1/2"
          data-testid={`validation-error-${column.id}`}
          title={validationError.message}
        >
          <AlertCircle className="h-4 w-4 text-red-500" />
        </div>
      )}
    </div>
  )
})

// Add display name to the component
ColumnHeader.displayName = 'ColumnHeader'

export const DataTableInput = React.forwardRef<
  { clearData: () => void; importData: (data: TableDataJSON) => void },
  DataTableInputProps
>(({
  onChange,
  initialData,
  minColumnWidth = 60,
  defaultColumnWidth = 120,
  maxColumnNameLength = 50,
  maxWidth,
  maxHeight = 400,
  storeRef
}, ref) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  
  // Create or use existing store
  const store = useMemo(() => {
    if (storeRef?.current) {
      return storeRef.current
    }

    const newStore = createDataTableStore({
      onChange,
      initialData,
      defaultColumnWidth,
      maxColumnNameLength
    })
    if (storeRef) {
      storeRef.current = newStore
    }
    return newStore
  }, [storeRef, onChange, initialData, defaultColumnWidth, maxColumnNameLength])

  // Expose methods through ref
  React.useImperativeHandle(ref, () => ({
    clearData: () => store.clearData(),
    importData: (data: TableDataJSON) => store.importData(data)
  }), [store]);

  // Use store selectors
  const data = store.useData()
  const columnDefs = store.useColumnDefs()
  const columnSizing = store.useColumnSizing()
  const focusPosition = store.useFocusPosition()
  const validationState = store.useValidationState()

  const handleColumnSizing: OnChangeFn<ColumnSizingState> = (updater) => {
    const newSizing = typeof updater === 'function' ? updater(columnSizing) : updater
    store.handleColumnSizing(newSizing)
  }

  const handleHeaderKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, columnId: string) => {
    store.handleKeyNavigation(e, -1, columnId)
  }

  // Focus effect
  useEffect(() => {
    if (focusPosition.rowIndex !== null && focusPosition.columnId !== null) {
      requestAnimationFrame(() => {
        const selector = focusPosition.rowIndex === -1
          ? `[data-testid="header-${focusPosition.columnId}"]`
          : `input[data-testid="cell-${focusPosition.rowIndex}-${focusPosition.columnId}"]`

        const element = document.querySelector(selector) as HTMLInputElement | null
        if (element) {
          element.focus()
          element.select()
        }
      })
    }
  }, [focusPosition])

  // Memoize column definitions with cell renderer
  const columns = React.useMemo(() => 
    columnDefs.map(col => ({
      id: col.id,
      accessorKey: col.id,
      enableResizing: true,
      size: (col.meta as { width: number })?.width || defaultColumnWidth,
      minSize: minColumnWidth,
      meta: col.meta || { name: `Column ${col.id}`, width: defaultColumnWidth },
      header: ({ column }) => (
        <ColumnHeader 
          key={column.id}
          column={column.columnDef as any}
          onRename={store.handleColumnRename}
          onKeyDown={handleHeaderKeyDown}
          store={store}
        />
      ),
      cell: ({ row, column }) => {
        const value = row.getValue<string>(column.id)
        return (
          <Input
            type="text"
            value={value ?? ''}
            onChange={e => store.handleCellChange(row.index, column.id, e.target.value)}
            onKeyDown={e => store.handleKeyNavigation(e, row.index, column.id)}
            onPaste={e => store.handlePaste(e, row.index, column.id)}
            className="h-8 px-2 border-0 rounded-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-blue-500"
            placeholder="Enter value..."
            data-row={row.index}
            data-col={column.id}
            data-testid={`cell-${row.index}-${column.id}`}
          />
        )
      }
    })) as ColumnDef<DataRowJSON>[]
  , [columnDefs.length, defaultColumnWidth, minColumnWidth, store])

  // Calculate total table width from column sizes
  const totalWidth = React.useMemo(() => {
    return Object.values(columnSizing).reduce((sum, width) => sum + width, 0)
  }, [columnSizing])

  // Initialize table with resizing configuration
  const table = useReactTable({
    data,
    columns,
    state: {
      columnSizing
    },
    onColumnSizingChange: handleColumnSizing,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      minSize: minColumnWidth,
      size: defaultColumnWidth,
      enableResizing: true
    },
    enableSorting: false,
    enableMultiSort: false,
    columnResizeDirection: 'ltr',
  })

  // Add importData method to the DOM element for direct access
  useEffect(() => {
    if (tableContainerRef.current) {
      const el = tableContainerRef.current;
      (el as any).clearData = store.clearData;
      (el as any).importData = (data: TableDataJSON) => {
        if (ref && 'current' in ref && ref.current) {
          ref.current.importData(data);
        }
      };
    }
  }, [store, ref]);

  const renderTable = () => (
    <div 
      className="w-full relative border rounded-md"
      style={{ maxHeight }}
    >
      <style dangerouslySetInnerHTML={{ __html: resizerStyles }} />
      <ScrollArea 
        className="border rounded-md h-full" 
        style={{ 
          maxWidth,
          minHeight: 0
        }}
      >
        <div style={{ width: Math.max(totalWidth, maxWidth || 0) }} className="min-w-full">
          <table className="w-full table-fixed relative">
            <thead className="top-0 bg-background">
              <tr className="relative">
                {table.getFlatHeaders().map(header => (
                  <th
                    key={header.id}
                    className="relative border-b p-0"
                    style={{ width: header.getSize() }}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {header.column.getCanResize() && (
                      <div
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        className={`resizer ${header.column.getIsResizing() ? 'isResizing' : ''} absolute top-0 right-0 h-full w-1 cursor-col-resize`}
                      />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.getRowModel().rows.map(row => (
                <tr key={row.id} className="relative border-b hover:bg-muted/50">
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} className="p-0 relative">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
      
      {/* Add button */}
      <div className="mt-2 flex justify-between">
        <Button
          size="sm"
          variant="outline"
          onClick={() => store.addRow()}
          className="text-xs"
        >
          Add Row
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => store.addColumn()}
          className="text-xs"
        >
          Add Column
        </Button>
      </div>
    </div>
  )

  return (
    <div className="h-full" data-testid="data-table-input" ref={tableContainerRef}>
      <style>{resizerStyles}</style>
      {renderTable()}
    </div>
  )
})

// Add display name to DataTableInput
DataTableInput.displayName = 'DataTableInput'